// Timeline Item Type Definitions with Component Mapping
// We only need to add new types and their corresponding components here.
// The rest of the code will automatically update based on this configuration.

export const TIMELINE_ITEM_CONFIG = {
  // Basic Dot Types
  dotBlue: { component: 'DotBlueItem', category: 'dot' },
  dotRed: { component: 'DotRedItem', category: 'dot' },
  dotGray: { component: 'DotGrayItem', category: 'dot' },
  dotDarkBlue: { component: 'AllColoursItem', category: 'dot' },
  dotGrey: { component: 'AllColoursItem', category: 'dot' },
  dotDiagnostic: { component: 'AllColoursItem', category: 'dot' },
  dotPurple: { component: 'AllColoursItem', category: 'dot' },
  dotBlueSmall: { component: 'DotBlueSmallItem', category: 'dot' },

  // Bell & Shield Types
  redBell: { component: 'BellShieldItem', category: 'bell_shield' },
  yellowBell: { component: 'BellShieldItem', category: 'bell_shield' },
  greenBell: { component: 'BellShieldItem', category: 'bell_shield' },
  shield: { component: 'BellShieldItem', category: 'bell_shield' },

  // Arrow & Icon Types
  greenZ: { component: 'GenericImgItem', category: 'arrow_icon' },
  yellowZ: { component: 'GenericImgItem', category: 'arrow_icon' },
  redZ: { component: 'GenericImgItem', category: 'arrow_icon' },
  greenArrowUp: { component: 'GenericImgItem', category: 'arrow_icon' },
  redArrowUp: { component: 'GenericImgItem', category: 'arrow_icon' },
  redArrowDown: { component: 'GenericImgItem', category: 'arrow_icon' },
  yellowArrowDown: { component: 'GenericImgItem', category: 'arrow_icon' },
  notesCircle: { component: 'GenericImgItem', category: 'arrow_icon' },
  bell: { component: 'GenericImgItem', category: 'arrow_icon' },
  purplePencil: { component: 'GenericImgItem', category: 'arrow_icon' },
  eye: { component: 'GenericImgItem', category: 'arrow_icon' },

  // Border Types
  redBorder: { component: 'BorderedItem', category: 'border' },
  yellowBorder: { component: 'BorderedItem', category: 'border' },
  transparentBorder: { component: 'BorderedItem', category: 'border' },

  // Gradient Types
  gradientGreenZ: { component: 'GradientItem', category: 'gradient' },
  gradientYellowZ: { component: 'GradientItem', category: 'gradient' },
  gradientRedZ: { component: 'GradientItem', category: 'gradient' },

  // Line Types
  lineMiddle: { component: 'LineMiddleItem', category: 'line' },
  lineMiddle3: { component: 'LineMiddleItem', category: 'line' },

  // Special Types
  action: { component: 'ActionItem', category: 'special' },
  prescription: { component: 'AllColoursItem', category: 'special' },
  allIcons: { component: 'AllIconsItem', category: 'special' },
  scriptWritten: { component: 'ScriptWrittenMedItem', category: 'medication' },
  homeMedication: { component: 'HomeMedItem', category: 'medication' },
  emrOrder: {
    component: 'MedicationRequestOnlyItem',
    category: 'medication',
  },
  emrOrderAndHome: {
    component: 'MedicationRequestHomeItem',
    category: 'medication',
  },
  dispense: { component: 'DispenseMhrMedItem', category: 'medication' },
} as const;

export type TimelineItemType = keyof typeof TIMELINE_ITEM_CONFIG;

export const DOT_TYPES = Object.keys(TIMELINE_ITEM_CONFIG).filter(
  (key) => TIMELINE_ITEM_CONFIG[key as TimelineItemType].category === 'dot',
);

export const BELL_SHIELD_TYPES = Object.keys(TIMELINE_ITEM_CONFIG).filter(
  (key) =>
    TIMELINE_ITEM_CONFIG[key as TimelineItemType].category === 'bell_shield',
);

export const ARROW_ICON_TYPES = Object.keys(TIMELINE_ITEM_CONFIG).filter(
  (key) =>
    TIMELINE_ITEM_CONFIG[key as TimelineItemType].category === 'arrow_icon',
);

export const BORDER_TYPES = Object.keys(TIMELINE_ITEM_CONFIG).filter(
  (key) => TIMELINE_ITEM_CONFIG[key as TimelineItemType].category === 'border',
);

export const GRADIENT_TYPES = Object.keys(TIMELINE_ITEM_CONFIG).filter(
  (key) =>
    TIMELINE_ITEM_CONFIG[key as TimelineItemType].category === 'gradient',
);

export const LINE_TYPES = Object.keys(TIMELINE_ITEM_CONFIG).filter(
  (key) => TIMELINE_ITEM_CONFIG[key as TimelineItemType].category === 'line',
);

export const SPECIAL_TYPES = Object.keys(TIMELINE_ITEM_CONFIG).filter(
  (key) => TIMELINE_ITEM_CONFIG[key as TimelineItemType].category === 'special',
);

export const MEDICATION_TYPES = Object.keys(TIMELINE_ITEM_CONFIG).filter(
  (key) =>
    TIMELINE_ITEM_CONFIG[key as TimelineItemType].category === 'medication',
);

export const ALL_TIMELINE_ITEM_TYPES = Object.keys(TIMELINE_ITEM_CONFIG);

export const TIMELINE_ITEM_RENDER_MAP = Object.entries(
  TIMELINE_ITEM_CONFIG,
).reduce(
  (acc, [type, config]) => {
    const componentName = config.component;
    if (!acc[componentName]) {
      acc[componentName] = [];
    }
    acc[componentName].push(type);
    return acc;
  },
  {} as Record<string, string[]>,
);

export const getComponentNameByItemType = (
  itemType: TimelineItemType,
): string | null => {
  return TIMELINE_ITEM_CONFIG[itemType]?.component || null;
};

export const getCategoryByItemType = (
  itemType: TimelineItemType,
): string | null => {
  return TIMELINE_ITEM_CONFIG[itemType]?.category || null;
};

export const getTypesByCategory = (category: string): string[] => {
  return Object.keys(TIMELINE_ITEM_CONFIG).filter(
    (key) =>
      TIMELINE_ITEM_CONFIG[key as TimelineItemType].category === category,
  );
};

export const getTypesByComponent = (componentName: string): string[] => {
  return Object.keys(TIMELINE_ITEM_CONFIG).filter(
    (key) =>
      TIMELINE_ITEM_CONFIG[key as TimelineItemType].component === componentName,
  );
};

export const isItemTypeInCategory = (
  itemType: TimelineItemType,
  category: string,
): boolean => {
  return TIMELINE_ITEM_CONFIG[itemType]?.category === category;
};

export const isItemTypeForComponent = (
  itemType: TimelineItemType,
  componentName: string,
): boolean => {
  return TIMELINE_ITEM_CONFIG[itemType]?.component === componentName;
};

export interface SurveyAnswers {
  id: string;
  question: string;
  answer: string;
}
